/**
 * Theme Bridge — CSS Custom Properties → ECharts Theme
 *
 * Reads the active design tokens from the DOM and produces a complete
 * ECharts theme object. Generates a harmonious 10-colour data palette
 * derived from the primary hue + semantic status colours.
 */

/**
 * Read a CSS custom property value from the document root.
 */
function getCSSVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Build a 10-colour data palette from the current theme tokens.
 * Uses primary shades + semantic status colours for variety.
 */
function buildDataPalette(): string[] {
  return [
    getCSSVar('--primary-500') || '#2d9b5e',
    getCSSVar('--info-500') || '#3b82f6',
    getCSSVar('--warning-500') || '#f59e0b',
    getCSSVar('--error-500') || '#ef4444',
    getCSSVar('--success-500') || '#22c55e',
    getCSSVar('--primary-300') || '#8dd3a8',
    getCSSVar('--info-600') || '#2563eb',
    getCSSVar('--warning-600') || '#d97706',
    getCSSVar('--primary-700') || '#1a623c',
    getCSSVar('--error-600') || '#dc2626',
  ];
}

/**
 * Detect whether the current theme is dark by checking if the computed
 * background luminance is low.
 */
function isDarkTheme(): boolean {
  const bg = getCSSVar('--surface-primary') || '#ffffff';
  // Simple heuristic: if #-prefixed hex, check first channel
  if (bg.startsWith('#')) {
    const r = parseInt(bg.slice(1, 3), 16);
    return r < 128;
  }
  return false;
}

export interface EChartsThemeConfig {
  color: string[];
  backgroundColor: string;
  textStyle: { color: string; fontFamily: string };
  title: { textStyle: { color: string }; subtextStyle: { color: string } };
  legend: { textStyle: { color: string } };
  tooltip: {
    backgroundColor: string;
    borderColor: string;
    textStyle: { color: string };
  };
  categoryAxis: {
    axisLine: { lineStyle: { color: string } };
    axisTick: { lineStyle: { color: string } };
    axisLabel: { color: string };
    splitLine: { lineStyle: { color: string } };
  };
  valueAxis: {
    axisLine: { lineStyle: { color: string } };
    axisTick: { lineStyle: { color: string } };
    axisLabel: { color: string };
    splitLine: { lineStyle: { color: string } };
  };
  [key: string]: unknown;
}

/**
 * Build a full ECharts theme configuration from current CSS custom properties.
 */
export function buildEChartsTheme(): EChartsThemeConfig {
  const palette = buildDataPalette();
  const dark = isDarkTheme();

  const textPrimary = getCSSVar('--text-primary') || (dark ? '#fafafa' : '#111827');
  const textSecondary = getCSSVar('--text-secondary') || (dark ? '#e5e5e5' : '#374151');
  const textMuted = getCSSVar('--text-muted') || (dark ? '#737373' : '#9ca3af');
  const surfaceTertiary = getCSSVar('--surface-tertiary') || (dark ? '#262626' : '#f3f4f6');
  const borderPrimary = getCSSVar('--border-primary') || (dark ? '#404040' : '#e5e7eb');
  const borderSecondary = getCSSVar('--border-secondary') || (dark ? '#262626' : '#f3f4f6');
  const fontFamily = getCSSVar('--font-family-base') || 'Inter, sans-serif';

  return {
    color: palette,
    backgroundColor: 'transparent',
    textStyle: { color: textPrimary, fontFamily },
    title: {
      textStyle: { color: textPrimary },
      subtextStyle: { color: textSecondary },
    },
    legend: {
      textStyle: { color: textSecondary },
    },
    tooltip: {
      backgroundColor: surfaceTertiary,
      borderColor: borderPrimary,
      textStyle: { color: textPrimary },
    },
    categoryAxis: {
      axisLine: { lineStyle: { color: borderPrimary } },
      axisTick: { lineStyle: { color: borderPrimary } },
      axisLabel: { color: textMuted },
      splitLine: { lineStyle: { color: borderSecondary } },
    },
    valueAxis: {
      axisLine: { lineStyle: { color: borderPrimary } },
      axisTick: { lineStyle: { color: borderPrimary } },
      axisLabel: { color: textMuted },
      splitLine: { lineStyle: { color: borderSecondary } },
    },
  };
}
